import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { Building2, CheckCircle } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_COUNTRIES = [
  'South Africa',
  'Namibia',
  'Botswana',
  'Zimbabwe',
  'Mozambique',
  'Eswatini',
  'Lesotho'
];

const BUSINESS_TYPES = [
  'Contractor',
  'System Integrator',
  'Reseller',
  'OEM'
];

const PartnerApply = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/partner/apply`, formData);
      toast.success('Application submitted successfully! We will review your application and contact you within 2-3 business days.');
      setTimeout(() => navigate('/partner-portal'), 2000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-ewss-navy">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnimatedSection>
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-ewss-cyan/10 border border-ewss-cyan rounded-full text-ewss-cyan text-sm font-semibold mb-6">
              Partner Application
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Apply for <span className="text-ewss-cyan">Partnership</span>
            </h1>
            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
              Join EWSS Automation's wholesale distribution network across Southern Africa
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Building2 className="text-ewss-cyan" size={24} />
                  Company Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      {...register('company_name', { required: 'Company name is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="Your Company (Pty) Ltd"
                    />
                    {errors.company_name && (
                      <p className="text-red-400 text-sm mt-1">{errors.company_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Registration Number *
                    </label>
                    <input
                      type="text"
                      {...register('registration_number', { required: 'Registration number is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="2021/123456/07"
                    />
                    {errors.registration_number && (
                      <p className="text-red-400 text-sm mt-1">{errors.registration_number.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      VAT Number (Optional)
                    </label>
                    <input
                      type="text"
                      {...register('vat_number')}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="4123456789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Business Type *
                    </label>
                    <select
                      {...register('business_type', { required: 'Business type is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                    >
                      <option value="">Select business type</option>
                      {BUSINESS_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.business_type && (
                      <p className="text-red-400 text-sm mt-1">{errors.business_type.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Country *
                    </label>
                    <select
                      {...register('country', { required: 'Country is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                    >
                      <option value="">Select country</option>
                      {ALLOWED_COUNTRIES.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    {errors.country && (
                      <p className="text-red-400 text-sm mt-1">{errors.country.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      {...register('city', { required: 'City is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="Johannesburg"
                    />
                    {errors.city && (
                      <p className="text-red-400 text-sm mt-1">{errors.city.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4 pt-6 border-t border-gray-700">
                <h3 className="text-xl font-semibold text-white">Contact Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      {...register('contact_person', { required: 'Contact person is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="John Doe"
                    />
                    {errors.contact_person && (
                      <p className="text-red-400 text-sm mt-1">{errors.contact_person.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="john@company.com"
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      {...register('phone', { required: 'Phone number is required' })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="+27 11 123 4567"
                    />
                    {errors.phone && (
                      <p className="text-red-400 text-sm mt-1">{errors.phone.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Years in Business *
                    </label>
                    <input
                      type="number"
                      {...register('years_in_business', { 
                        required: 'Years in business is required',
                        min: { value: 0, message: 'Must be 0 or greater' }
                      })}
                      className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                      placeholder="5"
                    />
                    {errors.years_in_business && (
                      <p className="text-red-400 text-sm mt-1">{errors.years_in_business.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div className="space-y-4 pt-6 border-t border-gray-700">
                <h3 className="text-xl font-semibold text-white">Business Details</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Estimated Monthly Volume (ZAR) *
                  </label>
                  <select
                    {...register('estimated_monthly_volume', { required: 'Please select estimated volume' })}
                    className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                  >
                    <option value="">Select volume range</option>
                    <option value="R10,000 - R50,000">R10,000 - R50,000</option>
                    <option value="R50,000 - R100,000">R50,000 - R100,000</option>
                    <option value="R100,000 - R250,000">R100,000 - R250,000</option>
                    <option value="R250,000 - R500,000">R250,000 - R500,000</option>
                    <option value="R500,000+">R500,000+</option>
                  </select>
                  {errors.estimated_monthly_volume && (
                    <p className="text-red-400 text-sm mt-1">{errors.estimated_monthly_volume.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    {...register('additional_notes')}
                    rows={4}
                    className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan transition-colors"
                    placeholder="Tell us more about your business and why you'd like to partner with EWSS..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 transition-colors disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Submit Application
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
};

export default PartnerApply;
